import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Download, Share, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// The beforeinstallprompt event is non-standard and not in the DOM lib types.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "enkey-pwa-install-dismissed";

// Detect iOS Safari, which does not support beforeinstallprompt and requires
// the manual "Add to Home Screen" flow via the Share sheet.
function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari =
    /safari/i.test(ua) && !/crios|fxios|chrome|android/i.test(ua);
  return isIos && isSafari;
}

// Whether the app is already running as an installed PWA.
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (
    window.navigator as unknown as { standalone?: boolean }
  ).standalone;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    iosStandalone === true
  );
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export default function PwaInstallBanner() {
  const { t } = useTranslation("common");
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosHintOnly, setIosHintOnly] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissed()) return;

    // Android / desktop Chrome: capture the install prompt for later.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Hide the banner once the app gets installed.
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: no install event, so surface the manual hint after a moment.
    if (isIosSafari()) {
      const timer = window.setTimeout(() => {
        setIosHintOnly(true);
        setVisible(true);
      }, 2500);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // best-effort
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div
      data-print-hidden
      className={cn(
        "enkey-ui-enter fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:bottom-4",
      )}
    >
      <div className="mx-auto flex w-full max-w-md items-start gap-3 rounded-2xl border border-border bg-card/95 p-4 shadow-2xl shadow-black/10 backdrop-blur-lg">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-lg shadow-primary/25">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold">
            {t("pwa.install.title")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {iosHintOnly
              ? t("pwa.install.ios_hint")
              : t("pwa.install.description")}
          </p>
          {!iosHintOnly && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={install} className="gap-1.5">
                <Download className="h-4 w-4" />
                {t("pwa.install.action")}
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss}>
                {t("pwa.install.dismiss")}
              </Button>
            </div>
          )}
          {iosHintOnly && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Share className="h-3.5 w-3.5" />
              <span>{t("pwa.install.action")}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("pwa.install.dismiss")}
          className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
